import { useState, useCallback, useMemo } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { ColumnDef } from "./types";

interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
  isRowClickable?: (row: T) => boolean;
  is_loading?: boolean;
  loadingRowCount?: number;
  emptyMessage?: string;
  is_fixed_layout?: boolean;
}

export function DataTable<T>({
  columns,
  data,
  getRowId,
  onRowClick,
  isRowClickable,
  is_loading = false,
  loadingRowCount = 5,
  emptyMessage = "No data found",
  is_fixed_layout = false,
}: DataTableProps<T>) {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const sortedData = useMemo(() => {
    if (!sortColumn) return data;

    const col = columns.find((c) => c.id === sortColumn);
    if (!col) return data;

    return [...data].sort((a, b) => {
      const aVal = col.sortValue ? col.sortValue(a) : "";
      const bVal = col.sortValue ? col.sortValue(b) : "";

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      const result = String(aVal).localeCompare(String(bVal));
      return sortDirection === "asc" ? result : -result;
    });
  }, [data, sortColumn, sortDirection, columns]);

  const handleSort = useCallback(
    (columnId: string) => {
      if (sortColumn === columnId) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortColumn(columnId);
        setSortDirection("asc");
      }
    },
    [sortColumn],
  );

  const getSortIcon = (columnId: string) => {
    if (sortColumn !== columnId)
      return <ArrowUpDown className="h-3 w-3 opacity-40" />;
    if (sortDirection === "asc") return <ArrowUp className="h-3 w-3" />;
    return <ArrowDown className="h-3 w-3" />;
  };

  const tableClassName = is_fixed_layout ? "sm:table-fixed" : undefined;

  if (is_loading) {
    return (
      <div className="border overflow-hidden">
        <Table className={tableClassName}>
          <TableHeader>
            <TableRow className="bg-muted/50">
              {columns.map((col) => (
                <TableHead key={col.id} className={col.width}>
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: loadingRowCount }).map((_, i) => (
              <TableRow key={i}>
                {columns.map((col) => (
                  <TableCell key={col.id}>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="border overflow-hidden">
      <div className="overflow-x-auto">
        <Table className={tableClassName}>
          <TableHeader>
            <TableRow className="bg-muted/50">
              {columns.map((col) => (
                <TableHead
                  key={col.id}
                  className={cn(
                    col.is_sortable !== false &&
                      "cursor-pointer hover:bg-muted/80 select-none",
                    col.width,
                  )}
                  onClick={
                    col.is_sortable !== false
                      ? () => handleSort(col.id)
                      : undefined
                  }
                >
                  <div className="flex items-center gap-1">
                    {col.header}
                    {col.is_sortable !== false && getSortIcon(col.id)}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedData.length > 0 ? (
              sortedData.map((row) => {
                const id = getRowId(row);
                const isClickable = isRowClickable
                  ? isRowClickable(row)
                  : !!onRowClick;
                return (
                  <TableRow
                    key={id}
                    className={cn(
                      isClickable && "cursor-pointer",
                      isClickable && "hover:bg-muted/50",
                    )}
                    onClick={isClickable ? () => onRowClick?.(row) : undefined}
                  >
                    {columns.map((col) => (
                      <TableCell key={col.id} className={col.width}>
                        {col.cell(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
